import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Zap } from "lucide-react";
import { toast } from "sonner";
import { getCustomers, getSettings, getLastReading, addBill } from "@/lib/api";

export default function BillGeneration() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [form, setForm] = useState({
    billDate: new Date().toISOString().split("T")[0],
    dueDate: "",
    previousReading: 0,
    currentReading: 0,
    conversionFactor: 1,
    rate: 25,
    serviceCharges: 50,
  });

  useEffect(() => {
    const load = async () => {
      setCustomers(await getCustomers());
      const s = await getSettings();
      setForm((f) => ({
        ...f,
        conversionFactor: s.conversionFactor || 1,
        rate: s.rate || 25,
        serviceCharges: s.serviceCharges || 50,
        dueDate: new Date(Date.now() + 15 * 86400000).toISOString().split("T")[0],
      }));
    };
    load();
  }, []);

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === selectedCustomerId),
    [customers, selectedCustomerId]
  );

  useEffect(() => {
    if (selectedCustomerId) {
      getLastReading(selectedCustomerId).then((last) =>
        setForm((f) => ({ ...f, previousReading: last }))
      );
    }
  }, [selectedCustomerId]);

  const consumedUnits = Math.max(0, form.currentReading - form.previousReading);
  const unitConsKgScm = consumedUnits * form.conversionFactor;
  const usageAmount = unitConsKgScm * form.rate;
  const netPayable = usageAmount + form.serviceCharges;

  const handleSave = async () => {
    if (!selectedCustomerId) { toast.error("Please select a customer"); return; }
    if (form.currentReading < form.previousReading) {
      toast.error("Current reading cannot be less than previous reading");
      return;
    }
    const bill = {
      customerId: selectedCustomerId,
      customerName: selectedCustomer.name,
      consumerNumber: selectedCustomer.consumerNumber,
      society: selectedCustomer.society,
      flatNo: selectedCustomer.flatNo,
      plotNo: selectedCustomer.plotNo,
      towerNo: selectedCustomer.towerNo,
      phone: selectedCustomer.phone,
      connectionType: selectedCustomer.connectionType,
      billDate: form.billDate,
      dueDate: form.dueDate,
      previousReading: form.previousReading,
      currentReading: form.currentReading,
      consumedUnits,
      conversionFactor: form.conversionFactor,
      unitConsKgScm,
      rate: form.rate,
      usageAmount,
      serviceCharges: form.serviceCharges,
      netPayable,
      isPaid: false,
      paidDate: null,
    };
    const saved = await addBill(bill);
    toast.success("Bill generated successfully!");
    navigate(`/bills/invoice/${saved.id}`);
  };

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1600px] mx-auto" data-testid="bill-generation-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">
          Generate Bill
        </h1>
        <Button
          data-testid="go-bulk-generate"
          variant="outline"
          onClick={() => navigate("/bills/bulk-generate")}
          className="rounded-sm border-zinc-300"
        >
          <Zap className="w-4 h-4 mr-2" /> Bulk Generate
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader>
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Select Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                <SelectTrigger data-testid="customer-select" className="bg-zinc-50 border-zinc-300 rounded-none">
                  <SelectValue placeholder="Choose a customer..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} — {c.consumerNumber}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCustomer && (
                <div className="mt-4 p-4 bg-zinc-50 border border-zinc-200 text-sm space-y-1">
                  <p><span className="text-zinc-500">Name:</span> <span className="font-medium">{selectedCustomer.name}</span></p>
                  <p><span className="text-zinc-500">Consumer No:</span> <span className="font-mono">{selectedCustomer.consumerNumber}</span></p>
                  <p><span className="text-zinc-500">Society:</span> {selectedCustomer.society}</p>
                  <p><span className="text-zinc-500">Connection:</span> {selectedCustomer.connectionType}</p>
                  <p><span className="text-zinc-500">Initial Reading:</span> <span className="font-mono font-semibold">{(selectedCustomer.initialReading || 0).toFixed(2)}</span></p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader>
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Meter Readings & Dates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bill Date</Label>
                  <Input data-testid="bill-date-input" type="date" value={form.billDate} onChange={(e) => updateField("billDate", e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input data-testid="due-date-input" type="date" value={form.dueDate} onChange={(e) => updateField("dueDate", e.target.value)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
                </div>
                <div>
                  <Label>Previous Reading</Label>
                  <Input data-testid="previous-reading-input" type="number" value={form.previousReading} onChange={(e) => updateField("previousReading", parseFloat(e.target.value) || 0)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
                </div>
                <div>
                  <Label>Current Reading</Label>
                  <Input data-testid="current-reading-input" type="number" value={form.currentReading} onChange={(e) => updateField("currentReading", parseFloat(e.target.value) || 0)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none">
            <CardHeader>
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Rate Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Conversion Factor</Label>
                  <Input data-testid="conversion-factor-input" type="number" step="0.01" value={form.conversionFactor} onChange={(e) => updateField("conversionFactor", parseFloat(e.target.value) || 0)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
                </div>
                <div>
                  <Label>Rate (per unit)</Label>
                  <Input data-testid="rate-input" type="number" step="0.01" value={form.rate} onChange={(e) => updateField("rate", parseFloat(e.target.value) || 0)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
                </div>
                <div>
                  <Label>Service Charges</Label>
                  <Input data-testid="service-charges-input" type="number" step="0.01" value={form.serviceCharges} onChange={(e) => updateField("serviceCharges", parseFloat(e.target.value) || 0)} className="bg-zinc-50 border-zinc-300 rounded-none mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card className="border border-zinc-200 rounded-none md:rounded-sm shadow-none sticky top-8">
            <CardHeader>
              <CardTitle className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Bill Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Consumed Units</span>
                  <span className="font-mono font-semibold" data-testid="consumed-units">{consumedUnits.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Unit Cons (KG/SCM)</span>
                  <span className="font-mono font-semibold" data-testid="unit-cons">{unitConsKgScm.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Usage Amount</span>
                  <span className="font-mono font-semibold" data-testid="usage-amount">₹{usageAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Service Charges</span>
                  <span className="font-mono font-semibold" data-testid="service-charges-display">₹{form.serviceCharges.toFixed(2)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Net Payable</span>
                  <span className="text-orange-600 font-mono" data-testid="net-payable">₹{netPayable.toFixed(2)}</span>
                </div>
              </div>
              <Button
                data-testid="generate-bill-button"
                onClick={handleSave}
                disabled={!selectedCustomerId}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-sm mt-4"
              >
                Generate Bill
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
